/**
 * HoursWorkedFieldset - "How long did this take?" — one hours input per
 * person on the crew, asked as a work order is handed off.
 *
 * This is the actual-hours half of estimate-versus-actual. The estimate is
 * the job's hour bucket (work_orders.allocated_hours), set up front by the
 * office; the numbers entered here land on each person's pivot row
 * (work_order_assignees.allocated_hours) and roll up into the job's total,
 * which is what the project pool charges and what gets approved for billing
 * on an hour-tracked job.
 *
 * It asks on EVERY work order, not just hour-tracked ones — an answer with no
 * estimate to compare against still tells the office what jobs like this
 * really take. `required` is what an hour-tracked job passes: the parent then
 * refuses to submit until every row carries a number, and this fieldset names
 * the gap.
 *
 * The crew itself is not edited here — that is the Job Details panel's job —
 * but someone missing from it can be added on the spot: the parent passes a
 * QuickAddUserLink as `quickAdd`, and the people it creates join `people`.
 * Controlled: the parent owns the hours map and the crew.
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface HoursWorkedPerson {
	/** User id — the key in the hours map. */
	id: string;
	/** Display name for the row. */
	name: string;
}

export interface HoursWorkedFieldsetProps {
	/** Everyone on the job, primary first. */
	people: HoursWorkedPerson[];
	/** Hours keyed by user id, as strings so a row can sit empty mid-typing. */
	hours: Record<string, string>;
	/** Called with the whole map on every keystroke. */
	onChange: (hours: Record<string, string>) => void;
	/** True on an hour-tracked job: every row must carry a positive number. */
	required?: boolean;
	/** The job's hour bucket — the estimate — when one was entered. */
	estimatedHours?: number | null;
	/** Optional "Quick Add User" control rendered under the rows (the parent's
	 *  QuickAddUserLink, wired to add the people it creates to `people`). */
	quickAdd?: React.ReactNode;
	/** Optional outer className merged onto the root element. */
	className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Names of the people whose row is empty or zero — what `required` gates on. */
export function peopleMissingHours(people: HoursWorkedPerson[], hours: Record<string, string>): string[] {
	return people
		.filter((person) => {
			const value = hours[person.id];
			return value === undefined || value === '' || !(Number(value) > 0);
		})
		.map((person) => person.name);
}

/** Total across the crew, ignoring blank rows. */
export function totalHoursWorked(hours: Record<string, string>): number {
	return Object.values(hours).reduce((sum, value) => {
		const n = Number(value);
		return sum + (value !== '' && Number.isFinite(n) ? n : 0);
	}, 0);
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const HoursWorkedFieldset: React.FC<HoursWorkedFieldsetProps> = ({
	people,
	hours,
	onChange,
	required = false,
	estimatedHours = null,
	quickAdd,
	className,
}) => {
	const total = totalHoursWorked(hours);
	const missing = required ? peopleMissingHours(people, hours) : [];
	const hasEstimate = typeof estimatedHours === 'number' && Number.isFinite(estimatedHours) && estimatedHours > 0;
	const variance = hasEstimate ? Number((total - (estimatedHours as number)).toFixed(2)) : 0;

	return (
		/* min-w-0: a fieldset defaults to min-inline-size: min-content, so it
		   never shrank below its longest name plus the hours box and spilled
		   out of any container narrower than that. */
		<fieldset className={`hours-worked-fieldset min-w-0 ${className ?? ''}`.trim()} data-ref="molecule-hours-worked-fieldset">
			<legend className="block text-14px font-sans font-semibold text-brand-text-muted mb-4px">
				Hours Worked{required ? '' : ' (optional)'}
			</legend>
			<p className="text-12px font-sans text-brand-text-muted mb-8px">How long the job took, per person.</p>

			<ul className="flex flex-col gap-8px">
				{people.map((person) => (
					<li key={person.id} className="flex items-center gap-8px">
						{/* Wraps rather than truncates: beside the hours box a
						    phone leaves the name about 136px, and a crew list
						    of "Bartholomew Feath…" can't be told apart. */}
						<span className="flex-1 min-w-0 text-14px font-sans text-brand-text break-words">{person.name}</span>
						<input
							type="number"
							min={0}
							step={0.25}
							inputMode="decimal"
							value={hours[person.id] ?? ''}
							onChange={(e) => onChange({ ...hours, [person.id]: e.target.value })}
							placeholder="hrs"
							aria-label={`Hours for ${person.name}`}
							className="w-[96px] px-10px py-8px text-16px font-sans text-brand-text text-right bg-white border border-gray-300 rounded-lg min-h-44px focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 focus:outline-none"
						/>
					</li>
				))}
			</ul>

			{/* Someone worked the job but isn't listed? Add them here. */}
			{quickAdd && <div className="hours-worked-fieldset__quick-add mt-4px">{quickAdd}</div>}

			{/* Total, against the estimate when there is one. Over-runs are
			    flagged, never blocked — the figure is settled at approval. */}
			<p className="mt-8px text-13px font-sans text-brand-text" data-ref="molecule-hours-worked-fieldset__total">
				<span className="font-semibold">{total}</span> hrs total
				{hasEstimate && (
					<span className="text-brand-text-muted">
						{` · estimated ${estimatedHours}`}
						{variance > 0 && <span className="text-red-600">{` · over by ${variance}`}</span>}
						{variance < 0 && <span className="text-green-700">{` · under by ${Math.abs(variance)}`}</span>}
					</span>
				)}
			</p>

			{missing.length > 0 && (
				<p className="mt-4px text-12px font-sans text-amber-700" role="status">
					{`This work order requires hours — still missing: ${missing.join(', ')}.`}
				</p>
			)}
		</fieldset>
	);
};

export default HoursWorkedFieldset;
